import { DraftSectionItem } from '../../../components/Section/Section.model'
import {
  DraftConjointItem,
  DraftEntryResponseOption,
  DraftTextHighlighterItem
} from '../../../data/gql-gen/questionnaire/graphql'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  DraftTextCardItem,
  EntryType,
  QuestionScaleLabel,
  QuestionTypeCode
} from '../../../data/model/questionnaire'
import { getQuestionEntryPrefix } from '../../../utils/questionnaireUtils'
import {
  MAXDIFF_LABEL_NAMES,
  SCALE_LABEL_NAMES
} from '../../../utils/surveyDocBuilder/model'
import {
  MarketVisibilityFilter,
  visibleInAllMarkets
} from '../../Questionnaire/MarketVisibility/marketVisibility'
import { FieldKind } from '../hooks/useSaveTranslation'

// The entry kinds the editor surfaces. Forks and sections are excluded —
// neither reaches a respondent.
export const TRANSLATABLE_ENTRY_TYPES: ReadonlyArray<EntryType> = [
  EntryType.QuestionEntryType,
  EntryType.MatrixEntryType,
  EntryType.TextCardEntryType,
  EntryType.TextHighlighterEntryType,
  EntryType.ConjointEntryType
]

/**
 * The questions a language has to cover, sections replaced by their contents.
 * Sections are never translated — they only group questions.
 *
 * Not `flattenEntries`: that one is parent-blind (right for display logic,
 * piping, masking and LOI, ~40 callers) whereas here hiding a section must hide
 * everything inside it, since a child doesn't repeat its section's markets.
 */
export const getTranslatableSurveyEntries = (
  entries: ReadonlyArray<DraftQuestionnaireEntry>,
  isVisible: MarketVisibilityFilter = visibleInAllMarkets
): DraftQuestionnaireEntry[] =>
  entries.flatMap((entry) => {
    if (entry.entryType === EntryType.SectionEntryType) {
      if (!isVisible(entry.hiddenInMarkets)) return []
      const item = entry.entryItem as DraftSectionItem
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      return (item.entries ?? []).filter(
        (child) =>
          TRANSLATABLE_ENTRY_TYPES.includes(child.entryType) &&
          isVisible(child.hiddenInMarkets)
      )
    }
    return TRANSLATABLE_ENTRY_TYPES.includes(entry.entryType) &&
      isVisible(entry.hiddenInMarkets)
      ? [entry]
      : []
  })

// A level can only be saved as part of a whole attribute payload, and `name` is
// non-null, so a level edit has to resend the attribute's name.
export interface ConjointAttributeRef {
  attributeId: string
  attributePosition: number
  // The stored translation only — never the master. Resending the master would
  // store it as the translation, which then reads as translated and latches:
  // completion counts it and auto-translate skips it for good. Resolves against
  // the extraction-time snapshot, so it misses a name translated earlier in the
  // same batch — see `orderPerFieldSaves`.
  resolveName: (languageCode: string) => string
  // Set on level fields only.
  optionId?: string
  optionPosition?: number
}

export interface TranslatableField {
  kind: FieldKind
  // questionLk / matrixTitleLk / textCardLk, or question.questionId for
  // highlighters and conjoints. Sub-items pass their owner's id with subId
  // carrying their own.
  entryId: string
  subId?: string
  // Conjoint attribute-name and level fields only.
  conjoint?: ConjointAttributeRef
  // Highlighter category fields only — required by the category input.
  highlighterCategory?: {
    colour: { hex: string; label: string }
    position: number
  }
  // Slider label fields only: 0 left, 1 central, 2 right.
  scalePosition?: number
  // Entry-prefix-based, human-readable key — e.g. "Q1.text", "T1.title",
  // "Q2.option.1", "Q3.row.2". The prefix is `getQuestionEntryPrefix(entry)`
  // — the SAME per-type label (T/Q/S/A + contextPosition+1) the survey editor
  // and the docx export use — so a leading text instruction reads "T1" here
  // exactly as it does everywhere else, and the keys line up 1:1 with the
  // per-type keys `projectDocxToTranslations` parses out of an exported doc.
  // Also used as the React list key (no UUIDs needed — the prefixes are unique
  // within a survey's translatable entries).
  key: string
  label: string
  master: string
  // Returns the current translation text for the given language, or '' if none.
  getCurrent: (languageCode: string) => string
  // Always true today — the hook for a family that should be editable but not
  // gate launch.
  countsTowardCompletion: boolean
}

const findResponseOptionTranslation = (
  option: DraftEntryResponseOption,
  languageCode: string
): string => {
  const translation = option.responseOption?.translations?.find(
    (t) => t.language === languageCode
  )
  return translation?.value ?? ''
}

const findQuestionTextTranslation = (
  question:
    | { translations?: { language: string; text: string }[] | null }
    | undefined
    | null,
  languageCode: string
): string => {
  const translation = question?.translations?.find(
    (t) => t.language === languageCode
  )
  return translation?.text ?? ''
}

const findMatrixTitleTranslation = (
  item: DraftMatrixItem,
  languageCode: string
): string => {
  const translation = item.translations?.find(
    (t) => t.language === languageCode
  )
  return translation?.title ?? ''
}

const findTextCardTranslation = (
  item: DraftTextCardItem,
  languageCode: string,
  key: 'title' | 'subtitle' | 'body'
): string => {
  const translation = item.translations?.find(
    (t) => t.language === languageCode
  )
  return translation?.[key] ?? ''
}

const findScaleLabelTranslation = (
  label: QuestionScaleLabel,
  languageCode: string
): string =>
  label.labelTranslations?.find((t) => t.language === languageCode)?.text ?? ''

// MaxDiff's three labels share one record per language, each field independently
// optional — an unset one reads as "not translated yet".
const findMaxDiffLabelTranslation = (
  item: DraftQuestionItem,
  languageCode: string,
  key: 'mostLabel' | 'leastLabel' | 'attributeLabel'
): string =>
  item.maxDiffLabelTranslations?.find((t) => t.language === languageCode)?.[
    key
  ] ?? ''

// A text highlighter keeps its question text AND its highlightable passage on
// the item's own translation record, not on `question.translations`.
const findHighlighterTranslation = (
  item: DraftTextHighlighterItem,
  languageCode: string,
  key: 'text' | 'content'
): string => {
  const translation = item.translations?.find(
    (t) => t.language === languageCode
  )
  if (!translation) return ''
  return key === 'text' ? translation.text : translation.content.text
}

const findConjointTextTranslation = (
  item: DraftConjointItem,
  languageCode: string
): string =>
  item.translations?.find((t) => t.language === languageCode)?.text ?? ''

const findConjointAttributeTranslation = (
  item: DraftConjointItem,
  languageCode: string,
  attributeId: string
) =>
  item.attributeTranslations?.find(
    (t) => t.language === languageCode && t.attributeId === attributeId
  )

/**
 * Extracts the per-field translation slots for a single questionnaire entry.
 * The field keys and card label are prefixed with `getQuestionEntryPrefix`
 * (per-type letter + `contextPosition + 1`), i.e. the exact label the survey
 * editor card and the docx export show for this entry — so numbering is
 * consistent across every surface.
 *
 * @param entry The draft questionnaire entry.
 * @param isVisible Drops options / rows no market in scope shows. Keys still
 *   number over the full list, so they stay stable for the DOCX round-trip.
 */
export const extractTranslatableFields = (
  entry: DraftQuestionnaireEntry,
  isVisible: MarketVisibilityFilter = visibleInAllMarkets
): TranslatableField[] => {
  const q = getQuestionEntryPrefix(entry)
  switch (entry.entryType) {
    case EntryType.QuestionEntryType: {
      const item = entry.entryItem as DraftQuestionItem
      const entryId = item.questionLk
      const fields: TranslatableField[] = []
      if (item.question) {
        fields.push({
          kind: 'questionText',
          entryId,
          key: `${q}.text`,
          label: 'Question text',
          master: item.question.text,
          getCurrent: (lang) =>
            findQuestionTextTranslation(item.question, lang),
          countsTowardCompletion: true
        })
      }
      item.responseOptions.forEach((opt, idx) => {
        const optId = opt.responseOption?.responseOptionId
        if (!optId) return
        if (!isVisible(opt.hiddenInMarkets)) return
        fields.push({
          kind: 'responseOptionValue',
          entryId,
          subId: optId,
          key: `${q}.option.${idx + 1}`,
          label: `Option ${idx + 1}`,
          master: opt.responseOption?.value ?? '',
          getCurrent: (lang) => findResponseOptionTranslation(opt, lang),
          countsTowardCompletion: true
        })
      })
      // A blank master label is one the researcher left empty — nothing to
      // translate, and it must not inflate the completion denominator.
      item.questionScale?.labels.forEach((label) => {
        if (!label.label.text.trim()) return
        fields.push({
          kind: 'scaleLabel',
          entryId,
          scalePosition: label.position,
          key: `${q}.scaleLabel.${label.position}`,
          label: SCALE_LABEL_NAMES[label.position] ?? `Label ${label.position}`,
          master: label.label.text,
          getCurrent: (lang) => findScaleLabelTranslation(label, lang),
          countsTowardCompletion: true
        })
      })
      // MaxDiff shows its own instruction labels either side of the options.
      // They live on the question entry but in a separate translation record,
      // so they only exist for max_diff questions.
      const maxDiff = item.maxDiffSpecification
      if (maxDiff && item.questionTypeCode === QuestionTypeCode.MaxDiff) {
        const labels = [
          {
            kind: 'maxDiffMostLabel',
            suffix: 'mostLabel',
            label: MAXDIFF_LABEL_NAMES.mostLabel,
            master: maxDiff.mostLabel.text
          },
          {
            kind: 'maxDiffLeastLabel',
            suffix: 'leastLabel',
            label: MAXDIFF_LABEL_NAMES.leastLabel,
            master: maxDiff.leastLabel.text
          },
          {
            kind: 'maxDiffAttributeLabel',
            suffix: 'attributeLabel',
            label: MAXDIFF_LABEL_NAMES.attributeLabel,
            master: maxDiff.attributeLabel.text
          }
        ] as const
        labels.forEach(({ kind, suffix, label, master }) => {
          fields.push({
            kind,
            entryId,
            key: `${q}.${suffix}`,
            label,
            master,
            getCurrent: (lang) =>
              findMaxDiffLabelTranslation(item, lang, suffix),
            countsTowardCompletion: true
          })
        })
      }
      return fields
    }
    case EntryType.MatrixEntryType: {
      const item = entry.entryItem as DraftMatrixItem
      const entryId = item.matrixTitleLk
      const fields: TranslatableField[] = []
      if (item.matrixTitle) {
        fields.push({
          kind: 'matrixTitle',
          entryId,
          key: `${q}.title`,
          label: 'Matrix title',
          master: item.matrixTitle.title,
          getCurrent: (lang) => findMatrixTitleTranslation(item, lang),
          countsTowardCompletion: true
        })
      }
      item.matrixRows.forEach((row, idx) => {
        const rowQuestionId = row.question?.questionId
        if (!rowQuestionId) return
        if (!isVisible(row.hiddenInMarkets)) return
        fields.push({
          kind: 'matrixRowText',
          entryId,
          subId: rowQuestionId,
          key: `${q}.row.${idx + 1}`,
          label: `Row ${idx + 1}`,
          master: row.question?.text ?? '',
          getCurrent: (lang) => findQuestionTextTranslation(row.question, lang),
          countsTowardCompletion: true
        })
      })
      item.responseOptions.forEach((opt, idx) => {
        const optId = opt.responseOption?.responseOptionId
        if (!optId) return
        if (!isVisible(opt.hiddenInMarkets)) return
        fields.push({
          kind: 'responseOptionValue',
          entryId,
          subId: optId,
          key: `${q}.col.${idx + 1}`,
          label: `Column ${idx + 1}`,
          master: opt.responseOption?.value ?? '',
          getCurrent: (lang) => findResponseOptionTranslation(opt, lang),
          countsTowardCompletion: true
        })
      })
      return fields
    }
    case EntryType.TextCardEntryType: {
      const item = entry.entryItem as DraftTextCardItem
      const entryId = item.textCardLk
      return [
        {
          kind: 'textCardTitle',
          entryId,
          key: `${q}.title`,
          label: 'Title',
          master: item.textCard.title,
          getCurrent: (lang) => findTextCardTranslation(item, lang, 'title'),
          countsTowardCompletion: true
        },
        {
          kind: 'textCardSubtitle',
          entryId,
          key: `${q}.subtitle`,
          label: 'Subtitle',
          master: item.textCard.subtitle,
          getCurrent: (lang) => findTextCardTranslation(item, lang, 'subtitle'),
          countsTowardCompletion: true
        },
        {
          kind: 'textCardBody',
          entryId,
          key: `${q}.body`,
          label: 'Body',
          master: item.textCard.body,
          getCurrent: (lang) => findTextCardTranslation(item, lang, 'body'),
          countsTowardCompletion: true
        }
      ]
    }
    case EntryType.TextHighlighterEntryType: {
      const item = entry.entryItem as DraftTextHighlighterItem
      // Highlighters and conjoints are addressed by their question id — that is
      // what the backend resolves `entryId` to for these two entry types.
      const entryId = item.question?.questionId
      if (!entryId) return []
      const fields: TranslatableField[] = [
        {
          kind: 'highlighterQuestionText',
          entryId,
          key: `${q}.text`,
          label: 'Question text',
          master: item.question?.text ?? '',
          getCurrent: (lang) => findHighlighterTranslation(item, lang, 'text'),
          countsTowardCompletion: true
        },
        {
          kind: 'highlighterContent',
          entryId,
          key: `${q}.passage`,
          label: 'Highlightable text',
          master: item.content.text,
          getCurrent: (lang) =>
            findHighlighterTranslation(item, lang, 'content'),
          countsTowardCompletion: true
        }
      ]
      // Categories look like response options but are not stored as them, so
      // `updateResponseOption` cannot resolve one — they save through the
      // highlighter, carrying the colour/position its input requires.
      item.highlighterCategories.forEach((category, idx) => {
        const optId = category.responseOption.responseOptionId
        fields.push({
          kind: 'highlighterCategory',
          entryId,
          subId: optId,
          highlighterCategory: {
            colour: {
              hex: category.colour.hex,
              label: category.colour.label
            },
            position: category.position
          },
          key: `${q}.category.${idx + 1}`,
          label: `Category ${idx + 1}`,
          master: category.responseOption.value,
          getCurrent: (lang) =>
            category.responseOption.translations?.find(
              (t) => t.language === lang
            )?.value ?? '',
          countsTowardCompletion: true
        })
      })
      return fields
    }
    case EntryType.ConjointEntryType: {
      const item = entry.entryItem as DraftConjointItem
      const entryId = item.question?.questionId
      if (!entryId) return []
      const fields: TranslatableField[] = [
        {
          kind: 'conjointText',
          entryId,
          key: `${q}.text`,
          label: 'Question text',
          master: item.question?.text ?? '',
          getCurrent: (lang) => findConjointTextTranslation(item, lang),
          countsTowardCompletion: true
        }
      ]
      item.attributes.forEach((attribute, attrIdx) => {
        const resolveName = (lang: string) =>
          findConjointAttributeTranslation(item, lang, attribute.attributeId)
            ?.name ?? ''
        fields.push({
          kind: 'conjointAttributeName',
          entryId,
          conjoint: {
            attributeId: attribute.attributeId,
            attributePosition: attribute.position,
            resolveName
          },
          key: `${q}.attribute.${attrIdx + 1}`,
          label: `Attribute ${attrIdx + 1}`,
          master: attribute.name,
          getCurrent: (lang) =>
            findConjointAttributeTranslation(item, lang, attribute.attributeId)
              ?.name ?? '',
          countsTowardCompletion: true
        })
        attribute.options.forEach((option, optIdx) => {
          fields.push({
            kind: 'conjointAttributeOption',
            entryId,
            conjoint: {
              attributeId: attribute.attributeId,
              attributePosition: attribute.position,
              resolveName,
              optionId: option.attributeOptionId,
              optionPosition: option.position
            },
            key: `${q}.attribute.${attrIdx + 1}.level.${optIdx + 1}`,
            label: `Attribute ${attrIdx + 1} — level ${optIdx + 1}`,
            master: option.value,
            getCurrent: (lang) =>
              findConjointAttributeTranslation(
                item,
                lang,
                attribute.attributeId
              )?.options.find(
                (o) => o.attributeOptionId === option.attributeOptionId
              )?.value ?? '',
            countsTowardCompletion: true
          })
        })
      })
      return fields
    }
    // Sections (and forks) reach no respondent, so nothing to translate.
    default:
      return []
  }
}

/**
 * Convenience helper: extract translatable fields across every entry in a
 * filtered list. Each entry's key/label comes from its own
 * `getQuestionEntryPrefix` (per-type, contextPosition-based), so the result is
 * independent of the list's order or filtering.
 */
export const extractAllSurveyFields = (
  entries: ReadonlyArray<DraftQuestionnaireEntry>,
  isVisible: MarketVisibilityFilter = visibleInAllMarkets
): TranslatableField[] =>
  entries.flatMap((entry) => extractTranslatableFields(entry, isVisible))
